import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { Bell, BellRinging, Trash, CaretUp, CaretDown, SealCheck, EnvelopeSimple } from "@phosphor-icons/react";
import { LineChart, Line, YAxis, XAxis, ReferenceLine, ResponsiveContainer, Tooltip } from "recharts";
import { SharePricesButton } from "@/components/SharePricesButton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPrice = (value, s) => {
  if (value === null || value === undefined) return "—";
  const sym = { EUR: "€", GBP: "", USD: "$" }[s.currency] || "";
  return `${sym}${Number(value).toFixed(2)}`;
};

const TrendSparkline = ({ points, target, series }) => {
  if (!points || points.length < 2) return null;
  const prices = points.map((p) => p.price);
  const t = parseFloat(target);
  let min = Math.min(...prices), max = Math.max(...prices);
  if (!isNaN(t)) { min = Math.min(min, t); max = Math.max(max, t); }
  const pad = (max - min) * 0.1 || 1;
  return (
    <div data-testid="alert-trend-chart">
      <p className="text-[10px] font-mono uppercase text-[#6B6661] mb-1">Recent trend ({points.length} wks) — amber line = your target</p>
      <div className="h-24">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <XAxis dataKey="date" hide />
            <YAxis domain={[min - pad, max + pad]} hide />
            <Tooltip
              formatter={(v) => [`${fmtPrice(v, series)} ${series.unit}`, series.label]}
              labelFormatter={(d) => `w/e ${d}`}
              contentStyle={{ fontSize: 11, padding: "4px 8px", border: "1px solid #E0D4C8" }}
            />
            <Line type="monotone" dataKey="price" stroke="#0F766E" strokeWidth={2} dot={false} isAnimationActive={false} />
            {!isNaN(t) && <ReferenceLine y={t} stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="4 3" />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-between text-[10px] font-mono text-[#6B6661]">
        <span>{points[0].date}</span>
        <span>{points[points.length - 1].date}</span>
      </div>
    </div>
  );
};

export default function PriceAlertsPage() {
  const { user, token } = useAuth();
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [condition, setCondition] = useState("above");
  const [target, setTarget] = useState("");
  const [creating, setCreating] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    axios.get(`${API}/price-alerts/series`, { timeout: 90000 })
      .then((res) => setSeries(res.data.series))
      .catch(() => toast.error("Failed to load price series"))
      .finally(() => setSeriesLoading(false));
  }, []);

  const fetchAlerts = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/price-alerts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAlerts(res.data.alerts))
      .catch(() => {});
  }, [token]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const openForm = (s) => {
    setSelected(s);
    setCondition("above");
    setTarget(s.current_price ? Number(s.current_price).toFixed(2) : "");
    setHistory(null);
    axios.get(`${API}/price-alerts/series/${s.id}/history`, { timeout: 90000 })
      .then((res) => setHistory(res.data.points))
      .catch(() => setHistory([]));
  };

  const createAlert = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Please sign in to set alerts"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/price-alerts`,
        { series_id: selected.id, condition, target_price: parseFloat(target) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Alert set — we'll email you when ${selected.label} goes ${condition} ${target}`);
      setSelected(null);
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create alert");
    } finally { setCreating(false); }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/price-alerts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Alert deleted");
      fetchAlerts();
    } catch { toast.error("Failed to delete alert"); }
  };

  const regions = ["Ireland", "UK", "USA"];

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEO title="Price Alerts" description="Set alerts on official cattle prices — Irish factory, AHDB GB deadweight and USDA — and get an email when your target is crossed." url="/alerts" />
      <Header />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">Price Alerts</span>
            <span className="px-2 py-1 bg-[#F6E9DD] text-[#6B6661] text-xs font-mono uppercase tracking-wider flex items-center gap-1"><SealCheck size={12} weight="fill" /> Official data only</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#111827] mb-2">Cattle Price Alerts</h1>
          <p className="text-[#6B6661] font-sans max-w-2xl flex items-center gap-2">
            <EnvelopeSimple size={16} className="text-[#0F766E]" />
            Pick an official price, set your target — we email you the moment it crosses. Alerts fire once, then switch off.
          </p>
          <div className="mt-3"><SharePricesButton /></div>
        </div>

        {!user && (
          <div className="bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3 mb-6" data-testid="alerts-signin-prompt">
            <p className="text-sm text-[#92400E]">Sign in (top right) to set alerts — you can still browse current official prices below.</p>
          </div>
        )}

        {regions.map((region) => {
          const regionSeries = series.filter((s) => s.region === region);
          if (!seriesLoading && regionSeries.length === 0) return null;
          return (
            <div key={region} className="mb-6">
              <h2 className="font-mono text-xs uppercase tracking-wider text-[#6B6661] mb-2">{region === "Ireland" ? "🇮🇪" : region === "UK" ? "🇬🇧" : "🇺🇸"} {region}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`series-grid-${region.toLowerCase()}`}>
                {seriesLoading ? [1, 2, 3].map((i) => <div key={i} className="bg-white border border-[#E0D4C8] h-28 animate-pulse" />)
                  : regionSeries.map((s) => (
                    <div key={s.id} className="bg-white border border-[#E0D4C8] p-4 hover:border-[#0F766E] transition-colors" data-testid={`series-card-${s.id}`}>
                      <p className="text-sm font-semibold text-[#33302E] mb-1">{s.label}</p>
                      <p className="text-[10px] font-mono text-[#6B6661] mb-2">{s.source}</p>
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-2xl font-mono text-[#111827]">{fmtPrice(s.current_price, s)}</span>
                          <span className="text-[10px] font-mono text-[#6B6661] ml-1">{s.unit}</span>
                        </div>
                        <button onClick={() => openForm(s)} disabled={s.current_price === null} data-testid={`set-alert-${s.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#F59E0B] text-[#33302E] text-xs font-semibold hover:bg-[#D97706] transition-colors disabled:opacity-40">
                          <Bell size={13} weight="bold" /> Set Alert
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          );
        })}

        {user && (
          <div className="mt-10">
            <h2 className="font-serif text-xl text-[#111827] mb-4 flex items-center gap-2"><BellRinging size={20} className="text-[#F59E0B]" /> My Alerts</h2>
            {alerts.length === 0 ? (
              <p className="text-sm text-[#6B6661] bg-white border border-[#E0D4C8] p-6 text-center" data-testid="no-alerts">No alerts yet — hit "Set Alert" on any price above.</p>
            ) : (
              <div className="bg-white border border-[#E0D4C8] overflow-x-auto">
                <table className="w-full text-sm" data-testid="my-alerts-table">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-left px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]">Price Series</th>
                      <th className="text-left px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]">Condition</th>
                      <th className="text-right px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]">Target</th>
                      <th className="text-center px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]">Status</th>
                      <th className="text-center px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {alerts.map((a) => (
                      <tr key={a.id} data-testid={`alert-row-${a.id}`}>
                        <td className="px-4 py-3 text-[#33302E]">{a.series_label}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-mono ${a.condition === "above" ? "text-[#059669]" : "text-[#DC2626]"}`}>
                            {a.condition === "above" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                            {a.condition}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[#33302E]">{fmtPrice(a.target_price, a)} {a.unit}</td>
                        <td className="px-4 py-3 text-center">
                          {a.active ? (
                            <span className="px-2 py-0.5 bg-[#ECFDF5] text-[#059669] text-[10px] font-mono uppercase">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-[#F6E9DD] text-[#6B6661] text-[10px] font-mono uppercase" title={a.triggered_at ? `Fired at ${fmtPrice(a.triggered_price, a)}` : ""}>
                              Fired{a.triggered_price ? ` @ ${fmtPrice(a.triggered_price, a)}` : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => deleteAlert(a.id)} data-testid={`delete-alert-${a.id}`} className="p-1.5 hover:bg-[#FEF2F2] transition-colors" title="Delete alert">
                            <Trash size={15} className="text-[#DC2626]" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSelected(null)}>
          <div className="bg-white border border-[#E0D4C8] w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="alert-form-modal">
            <div className="px-5 py-4 bg-[#022C22] flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg text-white">Set Price Alert</h3>
                <p className="text-xs text-[#6EE7B7]">{selected.label}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/60 hover:text-white text-xl leading-none">×</button>
            </div>
            <form onSubmit={createAlert} className="p-5 space-y-4">
              <div className="bg-[#F6E9DD] p-3">
                <span className="text-xs text-[#6B6661] block">Current official price</span>
                <span className="font-mono text-xl text-[#111827]">{fmtPrice(selected.current_price, selected)} <span className="text-xs text-[#6B6661]">{selected.unit}</span></span>
              </div>
              {history === null ? (
                <div className="h-24 bg-[#F9FAFB] animate-pulse" data-testid="alert-trend-loading" />
              ) : (
                <TrendSparkline points={history} target={target} series={selected} />
              )}
              <div>
                <label className="text-xs text-[#6B6661] block mb-2">Email me when the price goes</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setCondition("above")} data-testid="condition-above"
                    className={`flex-1 py-2.5 flex items-center justify-center gap-1 text-sm transition-colors ${condition === "above" ? "bg-[#059669] text-white" : "bg-[#F6E9DD] text-[#6B6661]"}`}>
                    <CaretUp size={14} weight="bold" /> Above
                  </button>
                  <button type="button" onClick={() => setCondition("below")} data-testid="condition-below"
                    className={`flex-1 py-2.5 flex items-center justify-center gap-1 text-sm transition-colors ${condition === "below" ? "bg-[#DC2626] text-white" : "bg-[#F6E9DD] text-[#6B6661]"}`}>
                    <CaretDown size={14} weight="bold" /> Below
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#6B6661] block mb-2">Target price ({selected.unit})</label>
                <input type="number" step="0.01" min="0.01" value={target} onChange={(e) => setTarget(e.target.value)} required data-testid="target-price-input"
                  className="w-full px-4 py-2.5 border border-[#E0D4C8] font-mono text-lg focus:outline-none focus:border-[#0F766E]" />
              </div>
              <button type="submit" disabled={creating || !target} data-testid="create-alert-submit"
                className="w-full py-3 bg-[#F59E0B] text-[#33302E] font-semibold hover:bg-[#D97706] transition-colors disabled:opacity-50">
                {creating ? "Setting alert..." : "Set Alert"}
              </button>
              <p className="text-[11px] text-[#6B6661] text-center">Checked every 30 minutes against official data. One email per alert — it deactivates after firing.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
